import { useEffect, useRef, useState } from "react";
import type { ReactNode } from "react";
import { ChevronDown } from "lucide-react";
import type { LucideIcon } from "lucide-react";
import { cn } from "@/lib/utils";

interface DropdownProps {
  trigger: ReactNode;
  align?: "left" | "right";
  children: ReactNode;
  className?: string;
}

export function Dropdown({ trigger, align = "left", children, className }: DropdownProps) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    window.addEventListener("mousedown", handler);
    return () => window.removeEventListener("mousedown", handler);
  }, [open]);

  return (
    <div ref={ref} className={cn("relative inline-block", className)}>
      <div onClick={() => setOpen((v) => !v)}>{trigger}</div>
      {open && (
        <div
          className={cn(
            "absolute z-50 mt-2 min-w-[200px] overflow-hidden rounded-xl border border-gray-100 bg-white shadow-xl",
            align === "right" ? "right-0" : "left-0",
          )}
          onClick={() => setOpen(false)}
        >
          {children}
        </div>
      )}
    </div>
  );
}

interface DropdownItemProps {
  icon?: LucideIcon;
  onClick?: () => void;
  variant?: "default" | "danger";
  disabled?: boolean;
  children: ReactNode;
}

export function DropdownItem({
  icon: Icon,
  onClick,
  variant = "default",
  disabled,
  children,
}: DropdownItemProps) {
  return (
    <button
      type="button"
      disabled={disabled}
      onClick={onClick}
      className={cn(
        "flex w-full items-center gap-3 px-4 py-2.5 text-left text-xs font-bold transition-colors disabled:cursor-not-allowed disabled:opacity-40",
        variant === "danger"
          ? "text-rose-600 hover:bg-rose-50"
          : "text-gray-700 hover:bg-indigo-50 hover:text-indigo-700",
      )}
    >
      {Icon && <Icon size={14} />}
      {children}
    </button>
  );
}

interface DropdownButtonProps {
  label: string;
  className?: string;
}

export function DropdownButton({ label, className }: DropdownButtonProps) {
  return (
    <button
      type="button"
      className={cn(
        "inline-flex items-center gap-2 rounded-xl border border-gray-100 bg-white px-4 py-2 text-[10px] font-black uppercase tracking-widest text-gray-600 transition-colors hover:border-indigo-200 hover:text-indigo-600",
        className,
      )}
    >
      {label}
      <ChevronDown size={12} />
    </button>
  );
}
